<template>
  <div class="!pt-0">
    <div class="flex flex-col space-y-2.5 max-h-screen pt-16 pb-2.5">
      <!-- Main Content Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto mt-2.5 relative">
        <!-- Header with Status and Actions -->
        <div
          class="p-6 border-b border-gray-200 bg-gradient-to-r "
          :class="[claimOverdue ? 'from-red-50 to-red-100' : 'from-gray-50 to-gray-100']"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-blue-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                </div>
              </div>

              <div class="flex-1">
                <div class="flex items-center space-x-3">
                  <h1 class="text-2xl font-bold text-gray-900">
                    {{ claimType }} Claim
                  </h1>

                  <template v-if="claim">
                    <span class="text-gray-400">•</span>

                    <h2 class="text-lg font-semibold text-gray-900">
                      Claim {{ claim.claim_number }}
                    </h2>

                    <span class="text-gray-400">•</span>

                    <h2 class="text-lg font-semibold text-gray-900">
                      {{ packageData?.name }}
                    </h2>

                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getStatusBadgeClass(claim.status)"
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full mr-1.5"
                        :class="getStatusBadgeDotClass(claim.status)"
                      />
                      {{ ClaimStatusTitles[enumKeyFromValue(ClaimStatus, claim.status)] }}
                    </span>
                  </template>

                  <div v-if="claimOverdue">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <span class="w-1.5 h-1.5 rounded-full mr-1.5 bg-red-400" />
                      Claim Overdue - you will need to lodge the claim amounts in the next claim period.
                    </span>
                  </div>
                </div>

                <div class="flex items-center space-x-3 mt-2">
                  <p
                    v-if="claim"
                    class="text-sm text-gray-600"
                  >
                    <span class="font-medium">Period</span> {{ new Date(claim.start).toDateString() }} - {{ new Date(claim.end).toDateString() }}
                  </p>

                  <span
                    v-if="claim && claim.draft_issued_at"
                    class="text-gray-400"
                  >•</span>

                  <p
                    v-if="claim && claim.draft_issued_at"
                    class="text-sm text-gray-600"
                  >
                    <span class="font-medium">Issued</span> {{ new Date(claim.draft_issued_at).toLocaleDateString() }}
                  </p>

                  <span
                    v-if="claim && claim.claim_issued_at"
                    class="text-gray-400"
                  >•</span>

                  <p
                    v-if="claim && claim.claim_issued_at"
                    class="text-sm text-gray-600"
                  >
                    <span class="font-medium">Submitted</span> {{ new Date(claim.claim_issued_at).toLocaleDateString() }}
                  </p>
                </div>

                <div class="mt-2">
                  <p
                    v-if="projectSupplier"
                    class="text-sm text-gray-600"
                  >
                    <span class="font-medium">Vendor</span> {{ projectSupplier.supplier?.name }}
                  </p>
                </div>

                <div
                  v-if="enableSopaStatement"
                  class="mt-3"
                >
                  <label
                    class="flex items-start space-x-2"
                  >
                    <input
                      v-model="sopaDisclaimer"
                      type="checkbox"
                      name="sopa_disclaimer"
                      data-test="sopa-disclaimer"
                      class="form-checkbox mt-0.5"
                      :class="canEditClaim ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'"
                      :disabled="!canEditClaim || sopaDisclaimerAction.is(States.LOADING)"
                      @change="saveSopaDisclaimer"
                    >

                    <span class="text-sm text-gray-700">
                      {{ sopaStatementText }}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center space-x-3">
              <button
                type="button"
                class="btn btn--secondary-blue flex items-center space-x-2"
                @click.prevent="showImageModal = true"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
</svg>

                <span>Attach Files</span>
              </button>

              <!-- Submit Claim Button -->
              <button
                v-if="claim?.status === ClaimStatus.draft_subcontractor && canEditClaim"
                type="button"
                class="btn btn--primary-blue flex items-center space-x-2"
                :class="{ 'opacity-50': submitAction.is(States.LOADING) }"
                :disabled="submitAction.is(States.LOADING)"
                @click="submitClaim"
              >
                <AppSpinner
                  v-if="submitAction.is(States.LOADING)"
                  class="w-4 h-4"
                />

                <svg

    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 v-else
                 
                  class="w-4 h-4">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
</svg>

                <span>Submit Claim</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Table Content -->
        <div class="overflow-x-auto">
          <RatesClaimTable
            v-if="!isLumpSum"
            :claim-id="claimId"
          />

          <LumpSumClaimTable
            v-else
            :claim-id="claimId"
          />
        </div>
      </div>
    </div>

    <ClaimAttachmentsModal
      v-if="showImageModal"
      :claim-id="claimId"
      @close="showImageModal = false;"
    />
  </div>
</template>

<script setup lang="ts">
import ClaimAttachmentsModal from '@/components/Guest/SubcontractorClaimCosts/ClaimAttachmentsModal.vue';
import LumpSumClaimTable from '@/components/Guest/SubcontractorClaimCosts/LumpSumClaimTable.vue';
import RatesClaimTable from '@/components/Guest/SubcontractorClaimCosts/RatesClaimTable.vue';
import { useSignedData } from '@/composables/useSignedData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { enumKeyFromValue, parseUrlParams, timeZone } from '@/helpers';
import Claim, { ClaimStatus, ClaimStatusTitles } from '@/models/Claim';
import LumpSumCost from '@/models/LumpSumCost';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import { useClaimsStore } from '@/store/claims';
import { useDayworksCostsStore } from '@/store/dayworksCosts';
import { useLaboursStore } from '@/store/labours';
import { useLumpSumCostsStore } from '@/store/lumpSumCosts';
import { usePackageClaimsStore } from '@/store/packageClaims';
import { usePackagesStore } from '@/store/packages';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { usePlantsStore } from '@/store/plants';
import { useProjectSupplierStore } from '@/store/projectSupplier';
import { useRatesCostsStore } from '@/store/ratesCosts';
import { useTradeItemsStore } from '@/store/tradeItems';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { useVariationOrderCostsStore } from '@/store/variationOrderCosts';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  claimId: string;
}>();

const signedParams = useSignedData();
const lumpSumCostsStore = useLumpSumCostsStore();
const ratesCostsStore = useRatesCostsStore();
const claimsStore = useClaimsStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const packagesStore = usePackagesStore();
const projectSupplierStore = useProjectSupplierStore();
const packageClaimsStore = usePackageClaimsStore();
const variationOrdersStore = useVariationOrdersStore();
const variationOrderCostsStore = useVariationOrderCostsStore();
const variationOrderCategoriesStore = useVariationOrderCategoriesStore();
const tradeItemsStore = useTradeItemsStore();
const dayworksCostsStore = useDayworksCostsStore();
const laboursStore = useLaboursStore();
const plantsStore = usePlantsStore();

// Save models from signed data
if(signedParams.models.lump_sum_costs) {
  lumpSumCostsStore.models.save(signedParams.models.lump_sum_costs);
}

if(signedParams.models.rates_costs) {
  ratesCostsStore.models.save(signedParams.models.rates_costs);
}

claimsStore.models.save(signedParams.models.claim);
packageTradeItemsStore.models.save(signedParams.models.package_trade_items);
tradeItemsStore.models.save(signedParams.models.tradeItems);
packagesStore.models.save(signedParams.models.package);
projectSupplierStore.models.save(signedParams.models.projectSupplier);
packageClaimsStore.models.save(signedParams.models.package_claim);

// Save variation orders and their costs
if(signedParams.models.variation_orders) {
  const variationOrders = signedParams.models.variation_orders;

  variationOrdersStore.models.save(variationOrders);
}

if(signedParams.models.variation_order_categories) {
  variationOrderCategoriesStore.models.save(signedParams.models.variation_order_categories);
}

if(signedParams.models.variation_order_costs) {
  variationOrderCostsStore.models.save(signedParams.models.variation_order_costs);
}

if(signedParams.models.dayworks_costs) {
  dayworksCostsStore.models.save(signedParams.models.dayworks_costs);
}

if(signedParams.models.labours) {
  laboursStore.models.save(signedParams.models.labours);
}

if(signedParams.models.plants) {
  plantsStore.models.save(signedParams.models.plants);
}

const claim = computed(() => {
  return claimsStore.models.find((signedParams.models.claim as Claim).id);
});

const projectSupplier = computed(() => {
  return projectSupplierStore.models.with('supplier').find((signedParams.models.projectSupplier as ProjectSupplier).id);
});

const packageData = computed(() => {
  return packagesStore.models.find((signedParams.models.package as Package).id);
});

const isLumpSum = computed(() => {
  return packageData.value?.procurement_method === 'lump_sum';
});

const claimType = computed(() => {
  return isLumpSum.value ? 'Lump Sum' : 'Rates';
});

const claimOverdue = computed(() => {
  return signedParams.meta.claim_overdue;
});

const canEditClaim = computed(() => {
  return signedParams.meta.edit_claim && claim.value?.status === ClaimStatus.draft_subcontractor && !claimOverdue.value;
});

const enableSopaStatement = computed(() => {
  return Boolean(signedParams.meta.enable_sopa_statement);
});

const sopaStatementText = computed(() => {
  return (signedParams.meta.sopa_statement_text as string) ?? '';
});

const submitAction = useStoreApiAction(claimsStore.submitSubcontractorClaim);
const sopaDisclaimerAction = useStoreApiAction(claimsStore.updateSubcontractorClaimSopaDisclaimer);
const sopaDisclaimer = ref(false);

watch(() => claim.value?.sopa_disclaimer, (value) => {
  if(value !== undefined) {
    sopaDisclaimer.value = value;
  }
}, { immediate: true });

const saveSopaDisclaimer = () => {
  if(!claim.value || !canEditClaim.value || !enableSopaStatement.value || !signedParams.meta.sopa_disclaimer_url) {
    return;
  }

  sopaDisclaimerAction.request(
    claim.value.id,
    parseUrlParams(signedParams.meta.sopa_disclaimer_url as string),
    { sopa_disclaimer: sopaDisclaimer.value },
  ).catch(() => {
    sopaDisclaimer.value = claim.value?.sopa_disclaimer ?? false;

    Swal.fire({
      icon: 'error',
      title: 'Failed to update disclaimer',
    });
  });
};

const submitClaim = async () => {
  const supplierEmail = projectSupplier.value?.contact_email;

  const result = await Swal.fire({
    icon: 'warning',
    title: 'Submit Claim',
    html: `
      <div class="text-left">
        <p class="mb-4">Are you sure you want to submit this claim? This cannot be undone.</p>
        <p class="mb-4 text-sm text-gray-600">
          A PDF copy of your claim will be emailed to:
          <strong>${supplierEmail || 'No contact email set'}</strong>
        </p>
        <p class="mb-4 text-sm text-gray-600">
          You can override the email address below if needed.
        </p>
      </div>
      <input
        id="email-input"
        type="email"
        class="swal2-input"
        placeholder="Override email (optional)"
        value="${supplierEmail || ''}"
      />
    `,

    showCancelButton: true,
    confirmButtonText: 'Submit Claim',
    cancelButtonText: 'Cancel',
    preConfirm: () => {
      const emailInput = document.getElementById('email-input') as HTMLInputElement;
      const email = emailInput?.value?.trim();

      if(!email && !supplierEmail) {
        Swal.showValidationMessage('Please provide an email address');

        return false;
      }

      if(email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        Swal.showValidationMessage('Please enter a valid email address');

        return false;
      }

      return email || supplierEmail;
    },

    allowOutsideClick: false,
    allowEscapeKey: true,
  });

  if(result.isConfirmed && result.value) {
    submitAction.request(claim.value.id, parseUrlParams(signedParams.meta.submit_claim_url as string), {
      claim_issued_at_time_zone: timeZone,
      email: result.value as string,
    }).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Claim submitted successfully',
      });
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to submit claim',
          text: error.data?.message,
        });
      }
    });
  }
};

const showImageModal = ref(false);

// Status badge helper functions
const getStatusBadgeClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-100 text-green-800';

    case ClaimStatus.draft_subcontractor:
      return 'bg-orange-100 text-orange-800';

    case ClaimStatus.subcontractor_claim:
      return 'bg-orange-100 text-orange-800';

    default:
      return 'bg-red-100 text-red-800';
  }
};

const getStatusBadgeDotClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-400';

    case ClaimStatus.draft_subcontractor:
      return 'bg-orange-400';

    case ClaimStatus.subcontractor_claim:
      return 'bg-orange-400';

    default:
      return 'bg-red-400';
  }
};
</script>

<style scoped></style>
